import './pages-css/AdvancedInstructions.css';

import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import type { SelectChangeEvent } from '@mui/material';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import { advancedInstructionOptions as config } from '../utils/config';

interface InstructionState {
  conciseness: string;
  tone: string;
  voice: string;
  checkboxes: Record<string, boolean>;
  freeText: string;
  [key: string]: any;
}

interface Props {
  onChange: (data: {
    instructionsText: string;
    json: InstructionState;
  }) => void;
  value: string;
}

const AdvancedInstructions = ({ onChange, value }: Props) => {
  const isInitialEditMode = Boolean(value?.trim());
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [state, setState] = useState<InstructionState>({
    conciseness: '',
    tone: '',
    voice: '',
    checkboxes: {},
    freeText: value || '',
    instructionHistory: [],
  });

  const [editOnlyMode, setEditOnlyMode] = useState<boolean>(isInitialEditMode);

  const toggleOpen = () => setIsOpen(!isOpen);

  const constructInstructions = (data: InstructionState) => {
    const instructions: string[] = [config.defaultInstruction];

    data.instructionHistory.forEach((key: any) => {
      if (['conciseness', 'tone', 'voice'].includes(key)) {
        const typedKey = key as 'conciseness' | 'tone' | 'voice';
        const option = config[typedKey].find(
          (opt) => opt.value === data[typedKey]
        );
        if (option?.autoText) instructions.push(option.autoText);
      } else {
        const checkbox = config.checkboxes.find((cb) => cb.value === key);
        if (data.checkboxes[key] && checkbox?.instruction) {
          instructions.push(checkbox.instruction);
        }
      }
    });

    if (data.freeText?.trim()) {
      instructions.push(data.freeText.trim());
    }

    return {
      instructionsText: instructions.join(' ').replace(/\s+/g, ' ').trim(),
      json: data,
    };
  };

  useEffect(() => {
    const initialData = constructInstructions(state);
    onChange(initialData);
  }, []);

  const handleClear = () => {
    const clearedState: InstructionState = {
      conciseness: '',
      tone: '',
      voice: '',
      checkboxes: {},
      freeText: '',
      instructionHistory: [], // Reset instruction order
    };

    setEditOnlyMode(false);
    setState(clearedState);
    onChange(constructInstructions(clearedState));
  };

  const handleDropdownChange =
    (key: keyof Pick<InstructionState, 'conciseness' | 'tone' | 'voice'>) =>
    (e: SelectChangeEvent<string>) => {
      const newValue = e.target.value;
      const updatedHistory = [...state.instructionHistory];

      // Only add to history if not already present
      if (!updatedHistory.includes(key)) {
        updatedHistory.push(key);
      }

      const updated: InstructionState = {
        ...state,
        [key]: newValue,
        instructionHistory: updatedHistory,
      };

      setState(updated);
      onChange(constructInstructions(updated));
    };

  const handleCheckboxChange =
    (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target;
      const updatedCheckboxes = { ...state.checkboxes, [key]: checked };

      let updatedHistory = [...state.instructionHistory];

      if (checked && !updatedHistory.includes(key)) {
        updatedHistory.push(key);
      } else if (!checked) {
        updatedHistory = updatedHistory.filter((item) => item !== key);
      }

      const updated: InstructionState = {
        ...state,
        checkboxes: updatedCheckboxes,
        instructionHistory: updatedHistory,
      };

      setState(updated);
      onChange(constructInstructions(updated));
    };

  return (
    <Box>
      <div
        style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}
      >
        <p className="font-inter text-lg font-medium">Advanced Instructions</p>
        <button
          type="button"
          className="toggle-textarea ml-2 text-center"
          style={{
            marginLeft: '8px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            paddingTop: '4px',
          }}
          onClick={toggleOpen}
        >
          {isOpen ? (
            <KeyboardArrowUpRoundedIcon />
          ) : (
            <KeyboardArrowDownRoundedIcon />
          )}
        </button>
      </div>

      <Collapse in={isOpen}>
        <Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: '4px' }}>
          {editOnlyMode ? (
            <>
              <TextField
                fullWidth
                multiline
                minRows={6}
                placeholder="additional instructions"
                value={state.freeText}
                margin="normal"
                size="small"
                disabled
              />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginTop: '8px',
                }}
              >
                <Button
                  onClick={handleClear}
                  variant="outlined"
                  sx={{
                    padding: '6px 12px',
                    borderColor: '#aaa',
                    color: '#fff',
                    backgroundColor: '#11ac9e',
                    '&:hover': {
                      backgroundColor: '#90bee6',
                    },
                  }}
                >
                  Clear
                </Button>
              </Box>
            </>
          ) : (
            <>
              <FormControl fullWidth margin="normal">
                <FormLabel>Conciseness</FormLabel>
                <Select
                  value={state.conciseness}
                  onChange={handleDropdownChange('conciseness')}
                  size="small"
                >
                  <MenuItem value="">Select...</MenuItem>
                  {config.conciseness.map((opt: any) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth margin="normal">
                <FormLabel>Tone</FormLabel>
                <Select
                  value={state.tone}
                  onChange={handleDropdownChange('tone')}
                  size="small"
                >
                  <MenuItem value="">Select...</MenuItem>
                  {config.tone.map((opt: any) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth margin="normal">
                <FormLabel>Voice</FormLabel>
                <Select
                  value={state.voice}
                  onChange={handleDropdownChange('voice')}
                  size="small"
                >
                  <MenuItem value="">Select...</MenuItem>
                  {config.voice.map((opt: any) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {config.checkboxes.map((cb: any) => (
                <FormControlLabel
                  key={cb.value}
                  control={
                    <Checkbox
                      checked={state.checkboxes[cb.value] || false}
                      onChange={handleCheckboxChange(cb.value)}
                    />
                  }
                  label={cb.label}
                />
              ))}

              <TextField
                fullWidth
                multiline
                minRows={6}
                placeholder="additional instructions"
                value={constructInstructions(state).instructionsText}
                margin="normal"
                size="small"
                disabled
              />
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default AdvancedInstructions;
